import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { PositionSchema } from '../../shared/position.schema';
/**
 * POST /api/v2/process/:id/draft/steps/insert-between — request
 *
 * Transactionally insert a new (viral) task step M between a source and its
 * current successors. **Id space: stableStepIds throughout** — the draft
 * graph's identity space, shared across draft steps, draft assignments and
 * event rows (matching the frontend Draft Graph keying). Backend `_id`s are
 * never accepted here.
 *
 * No `.refine`/`.superRefine` (bliksy resolveSubSchema); cross-field rules
 * are enforced service-side as 400s:
 * - `source.kind === 'start-event'` ⇒ exactly one target.
 * - `source.kind === 'node'` with `targetStableStepIds: []` ⇒ the source
 *   (task, or the named rule when `ruleId` is given) must currently be
 *   terminal.
 * - Every target must currently hold the source's successor edge (matching
 *   `ruleId` when given), else 409 `DRAFT_EDGE_NOT_FOUND`.
 * - `endPosition` ⇒ a terminal insert (`targetStableStepIds: []`).
 */ export const InsertBetweenSourceSchema = z.discriminatedUnion('kind', [
    /** The draft's start event row; its single outgoing edge is being split. */ z.object({
        kind: z.literal('start-event'),
        eventId: ObjectIdSchema
    }),
    /**
   * A draft step or assignment (shared `stableStepId` space). `ruleId`
   * scopes a decision source to one rule's output handle.
   */ z.object({
        kind: z.literal('node'),
        stableStepId: ObjectIdSchema,
        ruleId: ObjectIdSchema.optional()
    })
]);
export const InsertDraftStepBetweenRequestSchema = z.object({
    source: InsertBetweenSourceSchema,
    /** [] = terminal insert (step-and-end: M becomes the end). [one] = classic. [many] = fork capture. */ targetStableStepIds: z.array(ObjectIdSchema).max(100),
    position: PositionSchema,
    laneKey: z.string().optional(),
    /**
   * Terminal insert only: where the end marker sits. Written to the draft
   * layout under M's end node id, `end-<M.stableStepId>` (M is a task, so
   * no rule suffix).
   */ endPosition: PositionSchema.optional()
});

//# sourceMappingURL=insert-draft-step-between.schema.js.map